import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { noop } from "lodash";

import { GraphMetricCard } from "@similarweb/pro-logic-components";
import type { AreaGraphProps } from "@similarweb/pro-logic-components/dist/graphs";
import { Stack, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { percentageSignFilter } from "app/filters/ngFilters";
import { CardBenchmarkRow } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/components/CardBenchmarkRow";
import { useBrandInCitations } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/sources-report/hooks/useBrandInCitationsData";
import type { IBrandInCitationsMetricCardProps } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/sources-report/types";
import {
    getBaseWidgetRequestParams,
    getTopicsFilterParam,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import { ReportStatus } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";

import { getBrandInCitationsDateRange, getGraphDataFromArray, hasBrandInCitationsCoreAverage } from "../utils";

export const BrandInCitationsMetricCard = ({
    campaign,
    campaignConfig,
    reportStatus,
    reportDuration,
    graphProps,
}: IBrandInCitationsMetricCardProps) => {
    const {
        params: { topic: queryTopic },
    } = useCurrentStateAndParams();

    const canLoadData = reportStatus === ReportStatus.Ready && !!campaign;
    const topics = getTopicsFilterParam(queryTopic);

    const brandInCitationsDuration = useMemo(() => getBrandInCitationsDateRange(reportDuration), [reportDuration]);
    const isCapped = !brandInCitationsDuration.from.isSame(reportDuration.from);

    const { data, isLoading, isError } = useBrandInCitations(
        {
            ...getBaseWidgetRequestParams(campaign, campaignConfig, brandInCitationsDuration),
            topics,
        },
        { enabled: canLoadData },
    );

    const hasData = !!data && !isError && reportStatus !== ReportStatus.Error;
    const shouldShowLoading = (isLoading || !campaign) && reportStatus !== ReportStatus.Error;

    const brandInCitations = data?.brandInCitations;
    const showCoreAverage = hasBrandInCitationsCoreAverage(data);
    const graphData = useMemo<AreaGraphProps<unknown>["data"] | null>(
        () =>
            hasData && brandInCitations?.dailyData?.length
                ? getGraphDataFromArray(brandInCitations?.dailyData, "brand-in-citations")
                : null,
        [hasData, brandInCitations],
    );

    const secondaryValue = hasData ? (
        <Stack gap="2px">
            <Text fontVariant="queryBar1">
                {translate("llm.sources.overview.card.brand_in_citations.text", {
                    mentioned: (brandInCitations?.mentionCount ?? 0)?.toLocaleString(),
                    total: (brandInCitations?.numOfCitation ?? 0)?.toLocaleString(),
                })}
            </Text>
            {showCoreAverage ? (
                <CardBenchmarkRow
                    label={translate("llm.sources.overview.card.brand_in_citations.core_average")}
                    formattedValue={
                        percentageSignFilter()(data.competitorBenchmark.brandInCitationsScoreAvg, 1) as string
                    }
                    dataAutomation="brand-in-citations-core-average"
                />
            ) : null}
        </Stack>
    ) : null;

    return (
        <GraphMetricCard
            title={translate(
                isCapped
                    ? "llm.sources.overview.card.brand_in_citations.title.last_7_days"
                    : "llm.sources.overview.card.brand_in_citations.title",
            )}
            tooltip={translate("llm.sources.overview.card.brand_in_citations.tooltip")}
            value={hasData ? percentageSignFilter()(brandInCitations?.value) : null}
            secondaryValue={secondaryValue}
            graphProps={{
                ...graphProps,
                tooltipValueFormatter: (value: number) => percentageSignFilter()(value, 2),
                data: graphData,
            }}
            isLoading={shouldShowLoading}
            onCtaClick={noop}
        />
    );
};
